import { CommonModule } from '@angular/common';
import { Component, input, output, signal, ElementRef, viewChild, effect, HostListener } from '@angular/core';
import { IconComponent } from '../icon/icon.component';

export interface ButtonDropdownAction {
  id: string;
  label: string;
  icon?: string;
  disabled?: boolean;
}

@Component({
  selector: 'cm-button-dropdown',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './button-dropdown.component.html',
  styleUrl: './button-dropdown.component.less',
})
export class ButtonDropdownComponent {
  public readonly label = input.required<string>();
  public readonly icon = input<string>('bolt');
  public readonly actions = input.required<ButtonDropdownAction[]>();
  public readonly disabled = input<boolean>(false);
  public readonly autId = input<string>('button-dropdown');
  public readonly buttonClasses = input<string>('');

  public readonly actionClick = output<string>();

  protected readonly isOpen = signal<boolean>(false);
  protected readonly focusedIndex = signal<number>(-1);

  private readonly _dropdownRef = viewChild<ElementRef<HTMLDivElement>>('dropdown');
  private readonly _buttonRef = viewChild<ElementRef<HTMLButtonElement>>('button');

  constructor() {
    // Reset focused index when dropdown closes
    effect(() => {
      if (!this.isOpen()) {
        this.focusedIndex.set(-1);
      }
    });
  }

  @HostListener('document:click', ['$event'])
  protected _onDocumentClick(event: MouseEvent): void {
    const dropdown = this._dropdownRef()?.nativeElement;
    const button = this._buttonRef()?.nativeElement;
    const target = event.target as Node;

    if (dropdown && button && !dropdown.contains(target) && !button.contains(target)) {
      this._close();
    }
  }

  protected toggleDropdown(): void {
    if (this.disabled()) {
      return;
    }
    this.isOpen.set(!this.isOpen());
  }

  private _close(): void {
    this.isOpen.set(false);
  }

  protected onActionClick(actionId: string, actionDisabled?: boolean): void {
    if (actionDisabled) {
      return;
    }
    this.actionClick.emit(actionId);
    this._close();
  }

  protected onButtonKeydown(event: KeyboardEvent): void {
    if (this.disabled()) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen()) {
          this.isOpen.set(true);
        }
        this._focusNextItem();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!this.isOpen()) {
          this.isOpen.set(true);
        }
        this._focusPreviousItem();
        break;
      case 'Escape':
        event.preventDefault();
        this._close();
        this._buttonRef()?.nativeElement.focus();
        break;
      case 'Enter':
      case ' ':
        if (!this.isOpen()) {
          event.preventDefault();
          this.isOpen.set(true);
        }
        break;
    }
  }

  protected onMenuKeydown(event: KeyboardEvent, index: number): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this._focusNextItem();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this._focusPreviousItem();
        break;
      case 'Escape':
        event.preventDefault();
        this._close();
        this._buttonRef()?.nativeElement.focus();
        break;
      case 'Enter':
      case ' ': {
        event.preventDefault();
        const action = this.actions()[index];
        if (action && !action.disabled) {
          this.onActionClick(action.id);
        }
        break;
      }
      case 'Tab':
        this._close();
        break;
      case 'Home':
        event.preventDefault();
        this.focusedIndex.set(0);
        break;
      case 'End':
        event.preventDefault();
        this.focusedIndex.set(this.actions().length - 1);
        break;
    }
  }

  private _focusNextItem(): void {
    const currentIndex = this.focusedIndex();
    const actionsArray = this.actions();
    let nextIndex = currentIndex + 1;

    // Find next non-disabled item
    while (nextIndex < actionsArray.length) {
      if (!actionsArray[nextIndex].disabled) {
        this.focusedIndex.set(nextIndex);
        return;
      }
      nextIndex++;
    }

    // Wrap to first non-disabled item
    nextIndex = 0;
    while (nextIndex <= currentIndex && nextIndex < actionsArray.length) {
      if (!actionsArray[nextIndex].disabled) {
        this.focusedIndex.set(nextIndex);
        return;
      }
      nextIndex++;
    }
  }

  private _focusPreviousItem(): void {
    const currentIndex = this.focusedIndex();
    const actionsArray = this.actions();
    let prevIndex = currentIndex - 1;

    // Find previous non-disabled item
    while (prevIndex >= 0) {
      if (!actionsArray[prevIndex].disabled) {
        this.focusedIndex.set(prevIndex);
        return;
      }
      prevIndex--;
    }

    // Wrap to last non-disabled item
    prevIndex = actionsArray.length - 1;
    while (prevIndex >= currentIndex && prevIndex >= 0) {
      if (!actionsArray[prevIndex].disabled) {
        this.focusedIndex.set(prevIndex);
        return;
      }
      prevIndex--;
    }
  }
}
